// InvestigationsAvoidedDashboard — admin / MSP-scoped page at /admin/investigations-avoided.
// Surfaces the suppression telemetry: every detection match that WOULD have alerted but was
// auto-cleared by a suppression method. Tells the value story — "N benign alerts auto-cleared
// this period, by why" — and lets an analyst audit an individual suppression (drill-down
// renders the event like a real alert with a "Suppressed by: {label}" banner). Defaults to
// PROD-mode rules (the customer benefit number); TEST-mode suppressions are validation data,
// opt-in via the checkbox.
//
// The aggregate view reads the pre-aggregated ATS read model (no Databricks warehouse at view
// time); selecting a single tenant, or TEST-only, falls back to a live Delta query because the
// read model's leaf rows carry no tenant_id and hold no TEST keys. `summary.source` says which
// answered, and the two are NOT interchangeable numbers — see MEASURES below.
//
// MEASURES. These were conflated behind one COUNT(*) before the read model, and separating them
// is the point of this page:
//   * Investigations avoided (`investigationsAvoided`) — distinct suppression keys: the alerts
//     an analyst would actually have worked. THE headline.
//   * Suppressed matches (`total`) — every captured occurrence. Also the denominator behind
//     every breakdown percentage, which is why the breakdown tables count matches and never
//     investigations: the distinct measure is not additive across methods, and a non-additive
//     measure behind a % is simply a wrong number.
//
// VALUE TIER. Defaults to `unique` — suppressions where our own context was decisive. It is a
// deliberately much smaller number than "everything suppressed": a client who knows a competing
// tool would also have whitelisted the same offshore geographies will not credit "thousands of
// investigations prevented", and a smaller figure they believe is worth more than a larger one
// they discount. On the default view expect roughly 35–67 per weekday and 6–10 per weekend.
// A chart showing that is CORRECT — the weekend band is real seasonality, not a gap.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { fetchSuppressedAlertsSummary, fetchSuppressedAlertsEvents, fetchManagedTenants } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import { severityTone } from './ui/badgeTones';
import { RefreshButton, RefreshingBanner, LoadingCard, dataFadeClass } from './ui/LoadingState';
import Pagination from './Pagination';
import { SourceEventRenderer, RawEventJsonPanel, FormatTs, IpLink } from './sourceEvents';
import { TIMEZONE_OPTIONS, TZ_STORAGE_KEY, getDefaultTimezone, getTzLabel } from './TimelineView';
import { FilterCell, applyColFilters, countActiveFilters } from './coverage/coverageShared';
import { normalizeIsoUtc } from '../utils/timeFormat';

const FAMILY_TONE = {
  Baseline: 'info',
  Allowlist: 'purple',
  Network: 'info',
  Device: 'success',
  Identity: 'warning',
  Content: 'neutral',
  AiTM: 'critical',
  Other: 'neutral',
};

const PRESETS = [
  { id: 7, label: '7d' },
  { id: 30, label: '30d' },
  { id: 90, label: '90d' },
];

// How much credit a suppression deserves. `match_refinement` is excluded upstream at source,
// so it never appears here and nothing has to subtract it.
const VALUE_TIERS = [
  {
    id: 'unique',
    label: 'Our context was decisive',
    short: 'Decisive',
    blurb: 'Suppressions that needed context only we hold — device inventory, per-user baselines, tenant allowlists. The defensible client-facing number.',
  },
  {
    id: 'config_free_parity',
    label: 'A configured competing tool would also have caught',
    short: 'Parity',
    blurb: 'Real suppression, but a competitor with the same allowlists configured would have cleared these too. Genuine work saved; weak as a differentiator.',
  },
  {
    id: 'ALL',
    label: 'Every suppression',
    short: 'All',
    blurb: 'Both tiers combined. The largest number, and the least persuasive one to quote.',
  },
];

function isoDate(d) { return d.toISOString().slice(0, 10); }
function rangeForPreset(days) {
  const to = new Date();
  const from = new Date();
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { dateFrom: isoDate(from), dateTo: isoDate(to) };
}
function fmtNum(n) { return Number(n || 0).toLocaleString(); }

// Display label for a tenant in the Tenant dropdown — "(ShortName) Friendly Name"
// when both exist, else the best available identifier. Used for both the rendered
// option text and the alphabetical sort so the visible order matches the sort key.
function tenantOptionLabel(t) {
  const id = t.tenantId || t.tenantID;
  return (t.shortName && t.friendlyName) ? `(${t.shortName}) ${t.friendlyName}` : (t.friendlyName || t.shortName || t.domain || id);
}

export default function InvestigationsAvoidedDashboard() {
  const { user } = useAuth();

  const [preset, setPreset] = useState(7);
  const [mspCode, setMspCode] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [valueTier, setValueTier] = useState('unique');
  const [tenants, setTenants] = useState([]);

  const [selectedTz, setSelectedTz] = useState(getDefaultTimezone);
  const tzIana = useMemo(() => (TIMEZONE_OPTIONS.find((t) => t.id === selectedTz) || TIMEZONE_OPTIONS[0]).iana, [selectedTz]);
  useEffect(() => { try { localStorage.setItem(TZ_STORAGE_KEY, selectedTz); } catch { /* ignore */ } }, [selectedTz]);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ruleMode = includeTest ? 'ALL' : 'PROD';
  const { dateFrom, dateTo } = useMemo(() => rangeForPreset(preset), [preset]);

  useEffect(() => {
    let cancelled = false;
    fetchManagedTenants()
      .then((rows) => { if (!cancelled) setTenants(Array.isArray(rows) ? rows : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSuppressedAlertsSummary({
        dateFrom, dateTo, tenantId: tenantId || undefined, mspCode: mspCode || undefined, ruleMode, valueTier,
      });
      setSummary(data);
    } catch (err) {
      setError(err.message || 'Failed to load suppression summary');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, tenantId, mspCode, ruleMode, valueTier]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const mspOptions = useMemo(() => {
    const set = new Set();
    for (const t of tenants) {
      const code = (t.msp || '').toUpperCase();
      if (code) set.add(code);
    }
    return Array.from(set).sort();
  }, [tenants]);

  const tenantOptions = useMemo(() => {
    const base = !mspCode ? tenants : tenants.filter((t) => (t.msp || '').toUpperCase() === mspCode);
    return [...base].sort((a, b) =>
      tenantOptionLabel(a).localeCompare(tenantOptionLabel(b), undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [tenants, mspCode]);

  const handleMspChange = (code) => {
    setMspCode(code);
    // Drop the tenant selection if it no longer belongs to the chosen MSP.
    if (code && tenantId) {
      const t = tenants.find((x) => (x.tenantId || x.tenantID) === tenantId);
      if (!t || (t.msp || '').toUpperCase() !== code) setTenantId('');
    }
  };

  const topMethod = useMemo(() => {
    const t = summary?.byTier?.[0];
    return t ? t.label : '—';
  }, [summary]);

  // Breakdown percentages are always a share of suppressed MATCHES, never of
  // investigations avoided — the distinct measure does not sum across methods.
  const totalForPct = summary?.total || 0;

  // The Delta fallback has no value-tier dimension, so the selector would be lying if it
  // still showed a tier while a Delta result is on screen.
  const isDelta = summary?.source === 'delta';
  const activeTier = VALUE_TIERS.find((t) => t.id === (summary?.valueTier || valueTier)) || VALUE_TIERS[0];
  const avoided = Number(summary?.investigationsAvoided ?? summary?.distinctInvestigations ?? 0);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheckIcon className="h-7 w-7 text-emerald-500" />
          <h1 className="text-2xl font-bold text-gray-100">Investigations Avoided</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Every detection match that <strong>would have alerted</strong> but was automatically cleared by a
          suppression method — known device, baselined IP, trusted VPN, owner-authorized delegate, vendor
          file-hash FP, and more.
        </p>
      </div>

      {/* Controls */}
      <div className="card mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                preset === p.id ? 'bg-blue-600 border-blue-500 text-white' : 'border-gray-700 text-gray-400 hover:text-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
          <span className="ml-2 text-xs text-gray-600">{dateFrom} → {dateTo}</span>
        </div>

        {mspOptions.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">MSP</label>
            <select value={mspCode} onChange={(e) => handleMspChange(e.target.value)} className="input-field h-9 py-0 text-sm">
              <option value="">All MSPs</option>
              {mspOptions.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Tenant</label>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="input-field h-9 py-0 text-sm min-w-[16rem]">
            <option value="">{mspCode ? `All tenants in ${mspCode}` : 'All tenants'}</option>
            {tenantOptions.map((t) => {
              const id = t.tenantId || t.tenantID;
              return <option key={id} value={id}>{tenantOptionLabel(t)}</option>;
            })}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Credit</label>
          <select
            value={valueTier}
            onChange={(e) => setValueTier(e.target.value)}
            className="input-field h-9 py-0 text-sm"
            title={activeTier.blurb}
          >
            {VALUE_TIERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
          <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)}
            className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
          Include TEST-mode rules
        </label>

        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Time zone</label>
          <select value={selectedTz} onChange={(e) => setSelectedTz(e.target.value)} className="input-field h-9 py-0 text-sm">
            {TIMEZONE_OPTIONS.map((tz) => <option key={tz.id} value={tz.id}>{getTzLabel(tz.id)}</option>)}
          </select>
        </div>

        <RefreshButton loading={loading} onClick={loadSummary} className="btn-secondary flex items-center gap-1.5 text-xs ml-auto" />
      </div>

      {/* Refresh-in-progress banner (shown when reloading over existing data) */}
      <RefreshingBanner show={loading && !!summary} message="Refreshing suppression telemetry…" />

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={loadSummary} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {loading && !summary && <LoadingCard message="Querying suppression telemetry…" />}

      {summary && (
        <div className={dataFadeClass(loading)}>
          {/* Headline cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <HeadlineCard
              label={`Investigations avoided - ${windowLabel(summary)}`}
              value={fmtNum(avoided)}
              sub={isDelta ? 'distinct rule + user + tenant + day' : 'distinct suppression keys'}
              accent="text-emerald-400"
              icon={SparklesIcon}
            />
            <HeadlineCard
              label={`Suppressed matches - ${windowLabel(summary)}`}
              value={fmtNum(summary.total)}
              sub="every captured occurrence"
              accent="text-blue-400"
            />
            <HeadlineCard label={`Tenants with suppressions - ${windowLabel(summary)}`} value={fmtNum(summary.tenantCount)} accent="text-gray-100" />
            <HeadlineCard label="Top method" value={topMethod} accent="text-purple-300" small />
          </div>

          <ProvenanceStrip summary={summary} tier={activeTier} isDelta={isDelta} />

          {/* Daily trend */}
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-gray-300">Suppressions Per Day</h2>
              <div className="flex items-center gap-4 text-[11px] text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-0.5 bg-emerald-400" />Investigations avoided
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-0.5 bg-blue-500/60" />Suppressed matches
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 bg-gray-700/70 rounded-sm" />Weekend
                </span>
              </div>
            </div>
            {(summary.byDay || []).length === 0 ? (
              <p className="text-sm text-gray-600">No suppressions in this window.</p>
            ) : (
              <>
                <SuppressionTrendChart data={summary.byDay} />
                <p className="text-[11px] text-gray-600 mt-2">
                  {isDelta
                    ? 'Live query. \u201cSign-in from New Country\u201d country-baseline suppressions are counted once per user + country + day (repeat sign-ins collapsed); every other method counts each match.'
                    : 'Weekends are shaded: the avoided-investigations line runs materially lower on Saturday and Sunday every week, so a low weekend day is normal seasonality rather than a gap in the data.'}
                </p>
              </>
            )}
          </div>

          {/* Breakdown tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-300 mb-3">By Suppression Method</h2>
              <p className="text-[11px] text-gray-600 mb-2">
                Counts suppressed <strong>matches</strong>, so the bars are a share of {fmtNum(totalForPct)}.
                One cleared alert can satisfy several methods, so avoided-investigation counts cannot be split
                across this table.
              </p>
              <BreakdownTable
                rows={summary.byTier}
                cols={[
                  { key: 'label', label: 'Method', render: (r) => <span className="text-gray-200">{r.label}</span> },
                  { key: 'family', label: 'Family', render: (r) => <Badge tone={FAMILY_TONE[r.family] || 'neutral'}>{r.family}</Badge> },
                ]}
                total={totalForPct}
                empty="No suppressions."
              />
            </div>
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-300 mb-3">By Suppressed Alert Type</h2>
              <p className="text-[11px] text-gray-600 mb-2">
                Counts suppressed <strong>matches</strong>.
                {!isDelta && ' Severity is each rule\u2019s CURRENT setting, not its setting on the suppression date.'}
              </p>
              <BreakdownTable
                rows={summary.byRule}
                cols={[
                  { key: 'ruleName', label: 'Rule', render: (r) => <span className="text-gray-200 truncate">{r.ruleName}</span> },
                  { key: 'severity', label: 'Severity', render: (r) => r.severity ? <Badge tone={severityTone(r.severity)}>{r.severity}</Badge> : <span className="text-gray-600">—</span> },
                ]}
                total={totalForPct}
                empty="No suppressions."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-300 mb-3">By Severity</h2>
              <BreakdownTable
                rows={summary.bySeverity}
                cols={[{ key: 'severity', label: 'Severity', render: (r) => <Badge tone={severityTone(r.severity)}>{r.severity}</Badge> }]}
                total={totalForPct}
                empty="No suppressions."
              />
            </div>
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-300 mb-3">By MSP</h2>
              <BreakdownTable
                rows={summary.byMsp}
                cols={[
                  { key: 'displayName', label: 'MSP', render: (r) => (
                    <span className="text-gray-200">{r.displayName || r.mspCode}{r.displayName && r.mspCode && r.displayName !== r.mspCode ? <span className="text-gray-600 ml-1.5 text-xs">{r.mspCode}</span> : null}</span>
                  ) },
                  { key: 'tenantCount', label: 'Tenants', render: (r) => <span className="text-gray-400 text-xs">{fmtNum(r.tenantCount)}</span> },
                  // Distinct counts ARE valid per MSP and per tenant (a suppression key
                  // belongs to exactly one tenant, so those partitions are disjoint) —
                  // unlike the method/rule tables above.
                  { key: 'investigationsAvoided', label: 'Avoided', render: (r) => (
                    r.investigationsAvoided != null
                      ? <span className="text-emerald-400 text-xs tabular-nums">{fmtNum(r.investigationsAvoided)}</span>
                      : <span className="text-gray-600">—</span>
                  ) },
                ]}
                total={totalForPct}
                empty="No suppressions."
              />
            </div>
          </div>

          <div className="card mb-6">
            <h2 className="text-sm font-semibold text-gray-300 mb-3">By Customer</h2>
            <p className="text-xs text-gray-600 mb-3">Click a customer to audit its individual suppressed events.</p>
            <BreakdownTable
              rows={summary.byTenant}
              cols={[
                { key: 'friendlyName', label: 'Tenant', render: (r) => <span className="text-gray-200 truncate">{r.friendlyName}</span> },
                { key: 'mspCode', label: 'MSP', render: (r) => r.mspCode ? <Badge tone="info">{r.mspCode}</Badge> : <span className="text-gray-600">—</span> },
                { key: 'investigationsAvoided', label: 'Avoided', render: (r) => (
                  r.investigationsAvoided != null
                    ? <span className="text-emerald-400 text-xs tabular-nums">{fmtNum(r.investigationsAvoided)}</span>
                    : <span className="text-gray-600">—</span>
                ) },
              ]}
              total={totalForPct}
              empty="No suppressions."
              onRowClick={(r) => { if (r.tenantId) { setTenantId(r.tenantId); } }}
              activeRow={(r) => r.tenantId === tenantId}
            />
          </div>

          {/* Drill-down */}
          {tenantId ? (
            <DrillDown tenantId={tenantId} dateFrom={dateFrom} dateTo={dateTo} ruleMode={ruleMode} tzIana={tzIana} />
          ) : (
            <div className="card text-sm text-gray-500 flex items-center gap-2">
              <SparklesIcon className="h-4 w-4 text-gray-600" />
              Select a specific tenant above to audit individual suppressed events.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Compute a "nice" rounded axis maximum + tick step for an arbitrary data max,
// so the Y scale adapts to whatever the counts are (steps of 1/2/5 × 10^n → integer ticks).
function niceScale(max, ticks = 4) {
  if (!isFinite(max) || max <= 0) return { niceMax: 1, step: 1 };
  const rawStep = max / ticks;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let step;
  if (norm <= 1) step = 1;
  else if (norm <= 2) step = 2;
  else if (norm <= 5) step = 5;
  else step = 10;
  step *= mag;
  return { niceMax: Math.ceil(max / step) * step, step };
}

function mdLabel(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}` : (iso || '');
}

// Saturday/Sunday? Day keys are UTC yyyy-MM-dd, so read the UTC weekday (0=Sun, 6=Sat).
// The headline metric has a pronounced weekend trough (roughly 35-67 weekday vs 6-10
// weekend), so shading them keeps a normal Sunday from reading as an outage.
function isWeekendIso(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const wd = d.getUTCDay();
  return wd === 0 || wd === 6;
}

// Concise label for the window the summary actually covers, derived from the
// loaded summary's date range (this page exposes 7/30/90-day presets, so it reads
// "last N days"; "all time" when no bound). Always matches the data shown.
function windowLabel(summary) {
  const from = summary?.dateFrom;
  const to = summary?.dateTo;
  if (!from) return 'all time';
  try {
    const d1 = new Date(`${from}T00:00:00Z`);
    const d2 = new Date(`${to || from}T00:00:00Z`);
    const days = Math.round((d2 - d1) / 86400000) + 1;
    return `last ${days} day${days === 1 ? '' : 's'}`;
  } catch {
    return 'selected range';
  }
}

// Two-series line chart with labeled, variable-scale axes (X = date).
// Emerald = investigations avoided (the headline), muted blue = suppressed matches, on one
// shared scale so the gap between them reads as what it is: repeat suppressions of the same
// underlying finding. Weekend columns are shaded.
function SuppressionTrendChart({ data }) {
  const pts = Array.isArray(data) ? data : [];
  const n = pts.length;
  const W = 760, H = 220, padL = 48, padR = 16, padT = 12, padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const hasAvoided = pts.some((d) => d.investigationsAvoided != null);
  const maxCount = Math.max(1, ...pts.map((d) => Number(d.count) || 0));
  const { niceMax, step } = niceScale(maxCount, 4);
  const yTicks = [];
  for (let v = 0; v <= niceMax + 1e-9; v += step) yTicks.push(v);

  const xAt = (i) => (n <= 1 ? padL + plotW / 2 : padL + (i * plotW) / (n - 1));
  const yAt = (v) => padT + plotH - ((Number(v) || 0) / niceMax) * plotH;

  const linePts = pts.map((d, i) => `${xAt(i)},${yAt(d.count)}`).join(' ');
  const avoidedPts = hasAvoided
    ? pts.map((d, i) => `${xAt(i)},${yAt(Number(d.investigationsAvoided) || 0)}`).join(' ')
    : '';

  const maxLabels = 8;
  const everyX = Math.max(1, Math.ceil(n / maxLabels));
  const showX = (i) => i === n - 1 || i % everyX === 0;

  // Half-step band so a single-point series still gets a visible weekend marker.
  const bandW = n <= 1 ? plotW : plotW / (n - 1);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="select-none" style={{ maxHeight: '240px' }}>
      {/* Weekend bands, drawn first so every line and point sits on top of them */}
      {pts.map((d, i) => (isWeekendIso(d.date) ? (
        <rect
          key={`wk-${d.date}`}
          x={Math.max(padL, xAt(i) - bandW / 2)}
          y={padT}
          width={Math.min(bandW, W - padR - Math.max(padL, xAt(i) - bandW / 2))}
          height={plotH}
          fill="#374151"
          opacity="0.28"
        />
      ) : null))}
      {/* Y gridlines + labels */}
      {yTicks.map((v) => {
        const y = yAt(v);
        return (
          <g key={v}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#1f2937" strokeWidth="1" />
            <text x={padL - 8} y={y + 3} fontSize="11" fill="#9ca3af" textAnchor="end">{fmtNum(v)}</text>
          </g>
        );
      })}
      {/* Axes */}
      <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#4b5563" strokeWidth="1.25" />
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#4b5563" strokeWidth="1.25" />
      {/* X ticks + date labels */}
      {pts.map((d, i) => (showX(i) ? (
        <g key={d.date}>
          <line x1={xAt(i)} y1={H - padB} x2={xAt(i)} y2={H - padB + 4} stroke="#4b5563" strokeWidth="1" />
          <text x={xAt(i)} y={H - padB + 18} fontSize="11" fill="#9ca3af" textAnchor="middle">{mdLabel(d.date)}</text>
        </g>
      ) : null))}
      {/* Suppressed matches (context) */}
      {n >= 2 && <polyline points={linePts} fill="none" stroke="#60a5fa" strokeWidth="1.75" opacity="0.6" />}
      {pts.map((d, i) => (
        <circle key={`m-${d.date}`} cx={xAt(i)} cy={yAt(d.count)} r="2.75" fill="#60a5fa" fillOpacity="0.7" stroke="#0b1220" strokeWidth="1">
          <title>{`${d.date}: ${fmtNum(d.count)} suppressed matches`}</title>
        </circle>
      ))}
      {/* Investigations avoided (the headline) drawn last so it is never obscured */}
      {hasAvoided && n >= 2 && <polyline points={avoidedPts} fill="none" stroke="#34d399" strokeWidth="2.25" />}
      {hasAvoided && pts.map((d, i) => (
        <circle key={`a-${d.date}`} cx={xAt(i)} cy={yAt(Number(d.investigationsAvoided) || 0)} r="3.5" fill="#34d399" stroke="#0b1220" strokeWidth="1.25">
          <title>{`${d.date}${isWeekendIso(d.date) ? ' (weekend)' : ''}: ${fmtNum(d.investigationsAvoided)} investigations avoided, ${fmtNum(d.count)} matches`}</title>
        </circle>
      ))}
    </svg>
  );
}

// `computedUtc` arrives as PowerShell 'o' format — SEVEN fractional-second digits
// (2026-08-18T07:41:21.5154060Z). V8 tolerates that; Firefox and Safari return an
// Invalid Date, and `.toISOString()` on an Invalid Date THROWS, which would take the
// whole dashboard down rather than merely misprint a date. normalizeIsoUtc truncates to
// milliseconds; the isFinite guard means a future format change degrades to hiding one
// line instead of crashing. See TIMEZONE_DISPLAY_HISTORY.md — this bug class recurs.
function fmtComputedUtc(iso) {
  if (!iso) return '';
  const d = new Date(normalizeIsoUtc(iso));
  if (!Number.isFinite(d.getTime())) return '';
  return d.toISOString().replace('T', ' ').slice(0, 16);
}

// Where the figures came from and what qualifies them. Every line here is a caveat that
// would otherwise be invisible on screen: which source answered, how fresh it is, whether
// today's partial row is in the window, and whether the window starts before the telemetry
// itself does.
function ProvenanceStrip({ summary, tier, isDelta }) {
  const notes = [];

  if (isDelta) {
    notes.push(
      summary.ruleMode === 'TEST'
        ? 'Live query against Delta — the pre-aggregated read model holds no TEST-mode rows, so a TEST view has to be computed on demand.'
        : 'Live query against Delta — a single-tenant view cannot come from the read model, whose per-method rows are not broken out by tenant.'
    );
    notes.push('Credit tier does not apply to a live query, so this covers every suppression.');
  } else {
    notes.push(`Credit: ${tier.label.toLowerCase()} — ${tier.blurb}`);
  }

  if (summary.partialToday) {
    notes.push('Today is still accumulating and its rollup refreshes ~07:30 UTC, so the last point understates — a partial weekday can look like a quiet weekend.');
  }
  if (summary.coverageStart && summary.dateFrom && summary.coverageStart > summary.dateFrom) {
    notes.push(`Suppression telemetry begins ${summary.coverageStart}; earlier days in this range have no data rather than zero suppressions.`);
  }

  return (
    <div className="card mb-6 border-l-2 border-l-emerald-700/50">
      <div className="flex items-start gap-2">
        <SparklesIcon className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-gray-400 leading-relaxed space-y-1">
          {notes.map((nText, i) => <p key={i}>{nText}</p>)}
          {!isDelta && fmtComputedUtc(summary.computedUtc) && (
            <p className="text-gray-600">Rollup computed {fmtComputedUtc(summary.computedUtc)} UTC.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function HeadlineCard({ label, value, sub, accent = 'text-gray-100', small = false, icon: Icon }) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4 text-gray-600" />}
        <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      </div>
      <div className={`${small ? 'text-lg' : 'text-3xl'} font-bold ${accent} ${small ? 'truncate' : ''}`} title={small ? String(value) : undefined}>{value}</div>
      {sub && <div className="text-[10px] text-gray-600 mt-1">{sub}</div>}
    </div>
  );
}

function BreakdownTable({ rows, cols, total, empty, onRowClick, activeRow }) {
  const data = Array.isArray(rows) ? rows : [];
  if (data.length === 0) return <p className="text-sm text-gray-600">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-800 text-[10px] uppercase tracking-wider text-gray-500">
            {cols.map((c) => <th key={c.key} className="text-left font-semibold px-2 py-2">{c.label}</th>)}
            <th className="text-right font-semibold px-2 py-2">Count</th>
            <th className="w-28 px-2 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800/50">
          {data.map((r, i) => {
            const pct = total > 0 ? (r.count / total) * 100 : 0;
            const clickable = typeof onRowClick === 'function';
            const isActive = clickable && typeof activeRow === 'function' && activeRow(r);
            return (
              <tr
                key={i}
                onClick={clickable ? () => onRowClick(r) : undefined}
                className={`hover:bg-gray-800/30 ${clickable ? 'cursor-pointer' : ''} ${isActive ? 'bg-emerald-900/20' : ''}`}
              >
                {cols.map((c) => <td key={c.key} className="px-2 py-2 max-w-[14rem]">{c.render(r)}</td>)}
                <td className="px-2 py-2 text-right text-gray-300 tabular-nums">{fmtNum(r.count)}</td>
                <td className="px-2 py-2">
                  <div className="h-2 bg-gray-800 rounded overflow-hidden">
                    <div className="h-full bg-blue-600/70 rounded" style={{ width: `${Math.max(2, pct)}%` }} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Column config for the drill-down table. `filterType` drives the per-column filter
// row (reusing coverageShared's FilterCell + applyColFilters). `suppressedBy` is a
// derived field (suppressionLabel ?? suppressedByTier) normalized onto each event so
// the select filter has a single stable key.
const DRILLDOWN_COLS = [
  { key: 'eventTime', label: 'Event time', filterType: null },
  { key: 'ruleName', label: 'Rule', filterType: 'select' },
  { key: 'severity', label: 'Sev', filterType: 'select' },
  { key: 'suppressedBy', label: 'Suppressed by', filterType: 'select' },
  { key: 'userId', label: 'User', filterType: 'text' },
  { key: 'clientIp', label: 'IP', filterType: 'text' },
  { key: 'operation', label: 'Operation', filterType: 'text' },
];

function DrillDown({ tenantId, dateFrom, dateTo, ruleMode, tzIana }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpandedId(null);
    try {
      const data = await fetchSuppressedAlertsEvents({ tenantId, dateFrom, dateTo, ruleMode, top: 500 });
      setEvents(Array.isArray(data?.events) ? data.events : []);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message || 'Failed to load suppressed events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, dateFrom, dateTo, ruleMode]);

  useEffect(() => { load(); }, [load]);

  // Clear filters when the underlying query changes (new tenant / window / mode).
  useEffect(() => { setFilters({}); }, [tenantId, dateFrom, dateTo, ruleMode]);

  // Normalize the derived `suppressedBy` column onto each row so it can be filtered/sorted by key.
  const normalized = useMemo(
    () => events.map((e) => ({ ...e, suppressedBy: e.suppressionLabel || e.suppressedByTier || '' })),
    [events],
  );

  // Distinct values for the select filters.
  const distinct = useMemo(() => {
    const out = {};
    for (const col of DRILLDOWN_COLS) {
      if (col.filterType !== 'select') continue;
      const set = new Set();
      for (const e of normalized) { const v = e[col.key]; if (v) set.add(String(v)); }
      out[col.key] = Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    return out;
  }, [normalized]);

  const filtered = useMemo(() => applyColFilters(normalized, DRILLDOWN_COLS, filters), [normalized, filters]);
  const activeFilters = countActiveFilters(filters);

  const onFilterChange = useCallback((key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setCurrentPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, (safePage - 1) * pageSize + pageSize);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3 gap-3">
        <h2 className="text-sm font-semibold text-gray-300">Suppressed Events (Audit a Suppression)</h2>
        <div className="flex items-center gap-3">
          {activeFilters > 0 && (
            <button onClick={() => setFilters({})} className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
              <XMarkIcon className="h-3.5 w-3.5" />
              Clear filters ({activeFilters})
            </button>
          )}
          <span className="text-xs text-gray-500">
            {activeFilters > 0 ? `${filtered.length} of ${events.length}` : `${events.length} event${events.length !== 1 ? 's' : ''}`}
            {events.length >= 500 ? ' (capped at 500)' : ''}
          </span>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" /><span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="py-8 flex items-center justify-center gap-2 text-gray-500 text-sm">
          <ArrowPathIcon className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-600 py-4">No suppressed events for this tenant in the selected window.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="w-8" />
                  {DRILLDOWN_COLS.map((col) => (
                    <th key={col.key} className="text-left font-semibold px-3 py-2">{col.label}</th>
                  ))}
                </tr>
                <tr className="border-b border-gray-800 bg-gray-900/30">
                  <td className="w-8" />
                  {DRILLDOWN_COLS.map((col) => (
                    <td key={col.key} className="px-1.5 py-1 align-top">
                      {col.filterType ? (
                        <FilterCell col={col} filters={filters} onFilterChange={onFilterChange} distinct={distinct} />
                      ) : null}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {paged.length === 0 ? (
                  <tr><td colSpan={DRILLDOWN_COLS.length + 1} className="px-3 py-6 text-center text-sm text-gray-600">No events match the current filters.</td></tr>
                ) : paged.map((e) => {
                  const id = e.alertId || `${e.eventId}|${e.suppressedByTier}`;
                  const expanded = expandedId === id;
                  return <SuppressedRow key={id} e={e} id={id} expanded={expanded} tzIana={tzIana}
                    onToggle={() => setExpandedId(expanded ? null : id)} />;
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
          />
        </>
      )}
    </div>
  );
}

function SuppressedRow({ e, id, expanded, onToggle, tzIana }) {
  const rawEvent = useMemo(() => {
    if (!expanded || !e.rawEventJson) return null;
    try { return JSON.parse(e.rawEventJson); } catch { return null; }
  }, [expanded, e.rawEventJson]);

  return (
    <>
      <tr onClick={onToggle} className="hover:bg-gray-800/30 cursor-pointer">
        <td className="pl-3 pr-1 py-2">{expanded ? <ChevronDownIcon className="h-3.5 w-3.5 text-gray-500" /> : <ChevronRightIcon className="h-3.5 w-3.5 text-gray-500" />}</td>
        <td className="px-3 py-2 text-gray-300 whitespace-nowrap"><FormatTs ts={e.eventTime} tzIana={tzIana} /></td>
        <td className="px-3 py-2 text-gray-300 truncate max-w-[14rem]" title={e.ruleName}>{e.ruleName || '—'}</td>
        <td className="px-3 py-2">{e.severity ? <Badge tone={severityTone(e.severity)}>{e.severity}</Badge> : '—'}</td>
        <td className="px-3 py-2 text-gray-300 truncate max-w-[14rem]" title={e.suppressionLabel}>{e.suppressionLabel || e.suppressedByTier || '—'}</td>
        <td className="px-3 py-2 font-mono text-gray-300 truncate max-w-[12rem]" title={e.userId}>{e.userId || '—'}</td>
        <td className="px-3 py-2"><IpLink ip={e.clientIp} /></td>
        <td className="px-3 py-2 text-gray-300 truncate max-w-[10rem]" title={e.operation}>{e.operation || '—'}</td>
      </tr>
      {expanded && (
        <tr className="bg-gray-900/50 border-b-2 border-gray-700">
          <td colSpan={8} className="pl-10 pr-6 py-4 border-l-4 border-emerald-600/40">
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-2.5 rounded bg-emerald-900/20 border border-emerald-700/40 text-sm">
                <ShieldCheckIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                <span className="text-emerald-200">Suppressed by: <strong>{e.suppressionLabel || e.suppressedByTier}</strong></span>
                {e.ruleMode === 'TEST' && <Badge tone="neutral">TEST rule</Badge>}
              </div>
              {rawEvent ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Source Event (Microsoft data)</div>
                  <SourceEventRenderer rawEvent={rawEvent} logType={e.logType} tzIana={tzIana} />
                </div>
              ) : (
                <p className="text-xs text-gray-600">Raw event JSON could not be parsed for the structured view.</p>
              )}
              <RawEventJsonPanel rawEventJson={e.rawEventJson} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
